import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { AUTH_CONFIG } from '../auth/config.js';
import { getVerifier, getState, clearVerifier, clearState, buildAuthorizeUrl } from '../auth/pkce.js';
import { useUser } from '../context/user-context.jsx';

export default function Callback() {
	const [searchParams] = useSearchParams();
	const navigate = useNavigate();
	const [error, setError] = useState(null);
	const { setTokens } = useUser();

	useEffect(() => {
		async function exchangeCode() {
			const code = searchParams.get('code');
			const returnedState = searchParams.get('state');
			const storedState = getState();
			const codeVerifier = getVerifier();

			if (!code) {
				redirectToLoginWithError('Missing authorization code');
				return;
			}

			if (!returnedState || returnedState !== storedState) {
				redirectToLoginWithError('Invalid state parameter');
				return;
			}

			if (!codeVerifier) {
				redirectToLoginWithError('Missing code verifier');
				return;
			}

			try {
				const body = new URLSearchParams({
					grant_type: 'authorization_code',
					code,
					redirect_uri: AUTH_CONFIG.redirectUri,
					client_id: AUTH_CONFIG.clientId,
					code_verifier: codeVerifier,
				});

				const response = await fetch(`${AUTH_CONFIG.authServerUrl}${AUTH_CONFIG.endpoints.token}`, {
					method: 'POST',
					headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
					body,
				});

				if (!response.ok) {
					const errorData = await response.json().catch(() => ({}));
					throw new Error(errorData.error_description || errorData.error || 'Token exchange failed');
				}

				const tokens = await response.json();

				setTokens(tokens.access_token, tokens.refresh_token);

				clearVerifier();
				clearState();

				navigate('/overview', { replace: true });
			} catch (err) {
				redirectToLoginWithError(err.message || 'Token exchange failed');
			}
		}

		async function redirectToLoginWithError(message) {
			clearVerifier();
			clearState();
			setError(message);
			console.error('Auth callback error:', message);

			// Restart PKCE flow cleanly instead of passing error to authorize endpoint
			const loginUrl = await buildAuthorizeUrl();
			window.location.href = loginUrl;
		}

		exchangeCode();
	}, [searchParams, navigate, setTokens]);

	if (error) {
		return <div>Authentication error. Redirecting...</div>;
	}

	return <div>Completing sign in...</div>;
}
